import { useFeedback, useTranslation } from "@opendash/core";
import { _User } from "@opendash/plugin-parse/dist/types-generated";
import { Button, Form, Input, Modal, Select, Space } from "antd";
import Parse from "parse";
import React, { useState } from "react";
import { useLevels } from "../hooks/useLevels";
import { useLocationTypes } from "../hooks/useLocationTypes";
import { useStops } from "../hooks/useStops";
import { useWheelchairBoarding } from "../hooks/useWheelchairBoarding";
import { Level, Wheelchair_Boarding } from "../js/parsegenerated";
import { Location_Type } from "../js/parsegenerated/Location_Type";
import { Stop } from "../js/parsegenerated/Stop";
import { TimeZoneSelect } from "./TimeZoneSelect";

export interface StopDataType {
  stop_id: string;
  stop_code: string;
  stop_name: string;
  stop_desc: string;
  stop_lat: string;
  stop_lon: string;
  zone_id: string;
  stop_url: string;
  parent_station: Stop;
  stop_timezone: string;
  wheelchair_boarding: string;
  level_id: string;
  platform_code: string;
  location_type: number;
}

export const StopUpdateModal = (props: {
  open: boolean;
  stop: Stop;
  onOk: Function;
  onCancel: Function;
}) => {
  const t = useTranslation();
  const { message } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [locationTypes] = useLocationTypes();
  const [stops] = useStops();
  const [levels] = useLevels();
  const [wheelchair, reloadWheelchair] = useWheelchairBoarding("hasnoparent");
  const [selectedLocationType, setSelectedLocationType] = useState<number>(0);
  const { TextArea } = Input;
  const { Option } = Select;

  const isRequiredLocationType = (locationTypeValues: number[]) => {
    return locationTypeValues.some((value) => value === selectedLocationType);
  };

  // useEffect(() => {
  //   form.resetFields();
  // }, [props.stop]);

  const save = async () => {
    form.validateFields().then(async (values: StopDataType) => {
      setLoading(true);

      try {
        let parentstationobject: Stop;
        let levelobject: Level;
        let wheelchairobject: Wheelchair_Boarding;
        if (values.level_id) {
          levelobject = await new Parse.Query(Level).get(values.level_id);
        }
        if (values.parent_station) {
          parentstationobject = new Stop({
            objectId: values.parent_station.id,
          });
        }
        if (values.wheelchair_boarding) {
          wheelchairobject = await new Parse.Query(Wheelchair_Boarding).get(
            values.wheelchair_boarding
          );
        }
        const locationtypeobject = await new Parse.Query(Location_Type)
          .equalTo("value", values.location_type)
          .first();

        // const savedstop = await new Parse.Query(Stop).get(props.stop.id);

        await props.stop.save({
          id: props.stop.id,
          stop_id: values.stop_id,
          stop_code: values.stop_code,
          stop_name: values.stop_name,
          stop_desc: values.stop_desc,
          stop_lat: values.stop_lat,
          stop_lon: values.stop_lon,
          zone_id: values.zone_id,
          stop_url: values.stop_url,
          parent_station: parentstationobject,
          stop_timezone: values.stop_timezone,
          wheelchair_boarding: wheelchairobject,
          level_id: levelobject,
          platform_code: values.platform_code,
          location_type: locationtypeobject,
          tenant: (Parse.User.current() as _User).get("tenant"),
        });
        props.onOk();
        form.resetFields();
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
        message.error(t("gtfs:global.message.unknownerror"));
      }
    });
  };
  React.useEffect(() => {
    if (props.open) {
      form.setFieldsValue({
        stop_id: props.stop ? props.stop.get("stop_id") : null,
        stop_code: props.stop ? props.stop.get("stop_code") : null,
        stop_name: props.stop ? props.stop.get("stop_name") : null,
        stop_desc: props.stop ? props.stop.get("stop_desc") : null,
        stop_lat: props.stop ? props.stop.get("stop_lat") : null,
        stop_lon: props.stop ? props.stop.get("stop_lon") : null,
        zone_id: props.stop ? props.stop.get("zone_id") : null,
        stop_url: props.stop ? props.stop.get("stop_url") : null,
        parent_station: props.stop ? props.stop.get("parent_station") : null,
        stop_timezone: props.stop ? props.stop.get("stop_timezone") : null,
        wheelchair_boarding: props.stop
          ? props.stop.get("wheelchair_boarding").id
          : wheelchair[0]?.id || null,
        level_id: props.stop ? props.stop.get("level_id") : null,
        platform_code: props.stop ? props.stop.get("platform_code") : null,
        location_type: props.stop
          ? props.stop.get("location_type").get("value")
          : null,
      });
    }
  }, [props.stop, props.open]);

  return (
    <>
      <Modal
        title={t("gtfs:stop.create.modal.title")}
        open={props.open}
        onOk={save}
        onCancel={() => {
          form.resetFields();
          props.onCancel();
        }}
        width={"80%"}
        getContainer={false}
        footer={[
          <Button
            key="back"
            onClick={() => {
              props.onCancel();
            }}
          >
            {t("gtfs:global.button.cancel_label")}
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={save}>
            {t("gtfs:global.button.save_label")}
          </Button>,
        ]}
      >
        <Form
          autoComplete="off"
          form={form}
          layout="vertical"
          onValuesChange={(changedValues, values: StopDataType) => {
            setSelectedLocationType(values.location_type);

            // Reset the input of wheelchar_boarding so no wrong values can be stored.
            if (
              Object.keys(changedValues).some(
                (changedFormKeys) => changedFormKeys === "location_type"
              )
            ) {
              form.setFieldValue("wheelchair_boarding", null);
            }

            // Reload the data for wheelchair_boarding select
            if (values.location_type === 2) {
              reloadWheelchair("entryorexit");
            } else {
              if (values.parent_station) {
                reloadWheelchair("hasparent");
              } else if (!values.parent_station) {
                reloadWheelchair("hasnoparent");
              }
            }
          }}
        >
          <Form.Item
            name="stop_id"
            label={t("gtfs:stop.create.modal.form.id.label")}
            //help={t("gtfs:stop.create.modal.form.id.help")}
            initialValue={props.stop ? props.stop.id : null}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="stop_code"
            label={t("gtfs:stop.create.modal.form.stop_code.label")}
            initialValue={props.stop ? props.stop.get("stop_code") : null}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="stop_name"
            label={t("gtfs:stop.create.modal.form.stop_name.label")}
            initialValue={props.stop ? props.stop.get("stop_name") : null}
            rules={[
              {
                required: isRequiredLocationType([0, 1, 2]),
                message: t(
                  "gtfs:stop.create.modal.form.stop_name.rules.required"
                ),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="stop_desc"
            label={t("gtfs:stop.create.modal.form.stop_desc.label")}
            initialValue={props.stop ? props.stop.get("stop_desc") : null}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item>
            <Space.Compact>
              <Form.Item
                style={{ width: "50%" }}
                name="stop_lat"
                label={t("gtfs:stop.create.modal.form.stop_lat.label")}
                initialValue={props.stop ? props.stop.get("stop_lat") : null}
                rules={[
                  {
                    required: isRequiredLocationType([0, 1, 2]),
                    message: t(
                      "gtfs:stop.create.modal.form.stop_lat.rules.required"
                    ),
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                style={{ width: "50%" }}
                name="stop_lon"
                label={t("gtfs:stop.create.modal.form.stop_lon.label")}
                initialValue={props.stop ? props.stop.get("stop_lon") : null}
                rules={[
                  {
                    required: isRequiredLocationType([0, 1, 2]),
                    message: t(
                      "gtfs:stop.create.modal.form.stop_lon.rules.required"
                    ),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item
            name="zone_id"
            label={t("gtfs:stop.create.modal.form.zone_id.label")}
            initialValue={props.stop ? props.stop.get("zone_id") : null}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="stop_url"
            label={t("gtfs:stop.create.modal.form.stop_url.label")}
            initialValue={props.stop ? props.stop.get("stop_url") : null}
            rules={[
              {
                required: false,
                type: "url",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            initialValue={
              props.stop ? props.stop.get("location_type")?.get("value") : null
            }
            name="location_type"
            label={t("gtfs:stop.create.modal.form.location_type.label")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Select>
              {locationTypes.map((locationtype) => (
                <Option key={locationtype.id} value={locationtype.value}>
                  {locationtype.get("value") +
                    ": " +
                    locationtype.get("name") +
                    " - " +
                    locationtype.get("description")}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {isRequiredLocationType([0, 2, 3, 4]) && (
            <Form.Item
              name="parent_station"
              label={t("gtfs:stop.create.modal.form.parent_station.label")}
              initialValue={props.stop.get("parent_station")?.id || null}
              rules={[
                {
                  required: isRequiredLocationType([2, 3, 4]),
                  message: t(
                    "gtfs:stop.create.modal.form.parent_station.rules.required"
                  ),
                },
              ]}
            >
              <Select>
                {stops
                  .filter((stop) => {
                    switch (form.getFieldValue("location_type")) {
                      case 0:
                        return stop.get("location_type").get("value") === 1;
                      case 1:
                        return false;
                      case 2:
                        return stop.get("location_type").get("value") === 1;
                      case 3:
                        return stop.get("location_type").get("value") === 1;
                      case 4:
                        return stop.get("location_type").get("value") === 0;
                    }
                  })
                  .map((stop) => (
                    <Option key={stop.id} value={stop.id}>
                      {stop.get("stop_code") + ": " + stop.get("stop_name")}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="stop_timezone"
            label={t("gtfs:stop.create.modal.form.stop_timezone.label")}
            initialValue={props.stop ? props.stop.get("stop_timezone") : null}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <TimeZoneSelect />
          </Form.Item>

          <Form.Item
            name="wheelchair_boarding"
            label={t("gtfs:stop.create.modal.form.wheelchair_boarding.label")}
            initialValue={
              props.stop
                ? props.stop.get("wheelchair_boarding")?.id
                : wheelchair[0]?.id || null
            }
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Select>
              {wheelchair.map((wheely) => (
                <Option key={wheely.id} value={wheely.id}>
                  {wheely.get("value") + ": " + wheely.get("description")}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="level_id"
            help="Ebene des Orts. Ein und dieselbe Ebene kann von mehreren nicht verbundenen Stationen verwendet werden."
            label={t("gtfs:stop.create.modal.form.level_id.label")}
            initialValue={props.stop ? props.stop.get("level_id") : null}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Select>
              {levels.map((level) => (
                <Option key={level.id} value={level.id}>
                  {level.get("level_index") + ": " + level.get("level_name")}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="platform_code"
            help="Kennung für einen Bahnsteig (Haltestelle, die zu einer Station gehört). Hierfür sollte nur die Bahnsteigkennung verwendet werden (z. B. G oder 3). Begriffe wie „Bahnsteig“ oder „Gleis“ (oder die jeweilige Entsprechung in der Sprache des Feeds) sollten nicht enthalten sein. Das erleichtert Feednutzern die Internationalisierung und Lokalisierung der Bahnsteigkennung für andere Sprachen"
            label={t("gtfs:stop.create.modal.form.platform_code.label")}
            initialValue={props.stop ? props.stop.get("platform_code") : "0"}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
